import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Route } from 'react-router-dom';
import { useQuery } from 'urql';
import { AuthGate } from '~/components/AuthGate';
import { CreateButton } from '~/components/CreateButton';
import { FloatingActionButton } from '~/components/FloatingActionButton';
import { ItemForm } from '~/components/item/ItemForm';
import { ListDetailView } from '~/components/ListDetailView';
import { FilterGroupSearch } from '~/components/ui/FilterGroupSearch';
import { MatchList } from '~/components/ui/MatchList';
import { NavContainer } from '~/components/ui/NavContainer';
import { DisplayState, StatefulView } from '~/components/ui/StatefulView';
import { withModal } from '~/components/withModal';
import { getFragmentData, graphql } from '~/gql';
import { ItemsQuery, ItemType, Status } from '~/gql/graphql';
import { useLocalStorage } from '~/hooks'; // Or the correct path
import { useDynamicHeaderMargin } from '~/hooks/useDynamicHeaderMargin'; // Import the hook
import { useEnumOptions } from '~/hooks/useEnumOptions';
import { useSearch } from '~/hooks/useSearch'; // Import useSearch
import { useSort } from '~/hooks/useSort';
import { StockNav } from '~/layouts/nav/StockNav';
import { ItemView } from './[id]';

export const ItemsQueryDocument = graphql(`
  query Items($status: [Status!], $inConfig: Boolean) {
    items(status: $status, inConfig: $inConfig) {
      id
      ...itemData
    }
  }
`);

export const ItemData_ItemFragment = graphql(`
  fragment itemData on Item {
    id
    status
    name
    type
    description
    skuPrefix
    images
    image
    activeSkus
    hasVariants
    attributes {
      ...AttributeFields
    }
    skus {
      id
      status
      _name
      name
      code
      images
      image
      variant {
        name
        value
      }
    }
    variantTypes {
      name
      values
    }
  }
`);

export type Item = ItemsQuery['items'][0];

const ItemFormModal = withModal(ItemForm);
const ItemViewModal = withModal(ItemView);

// Define the new props expected from the parent
type FilterProps = {
  value: {
    searchValue: string;
    filtersValue: URLSearchParams;
  };
  onChange: (value: {
    searchValue: string;
    filtersValue: URLSearchParams;
  }) => void;
};

const ItemFilters = ({ value, onChange }: FilterProps) => {
  const { t } = useTranslation();
  const itemTypeOptions = useEnumOptions(ItemType, 'itemType');
  const statusOptions = Object.values(Status).map((value) => ({
    value,
    label: t(`statuses.${value}`), // Translate each status label
  }));

  return (
    <FilterGroupSearch
      filters={[
        // Status Filter
        {
          name: 'status',
          label: t('status'),
          options: statusOptions,
          type: 'select',
          multiple: true, // Assuming multiple statuses can be selected
          searchable: false, // Status list is likely short
        },
        // Type Filter - NEEDS BACKEND CHANGE TO WORK
        // {
        //   name: 'type',
        //   label: t('itemTypeLabel'),
        //   options: itemTypeOptions,
        //   type: 'select',
        //   multiple: true,
        //   searchable: true, // Type list might be longer
        // },
      ]}
      value={value}
      onChange={onChange} // Pass onChange handler down
      placeholder={'Find Items...'}
      // Add sort options here if needed later
    />
  );
};

const SEARCH_OPTIONS = {
  keys: ['name', 'skus.code', 'skus.name', 'status', 'type'],
};

export const Items = () => {
  const ls = useLocalStorage(); // Instantiate the hook
  const { t } = useTranslation(); // Added missing useTranslation call
  const { filterRowRef, dynamicMarginTop } = useDynamicHeaderMargin(); // Call the hook

  // State for Search
  const [searchValue, setSearchValue] = useState('');

  // State for Filters (already exists, ensures default status)
  const [filtersValue, setFiltersValue] = useState<URLSearchParams>(() => {
    const savedFilters = ls.get('items.filters');
    if (savedFilters) {
      return new URLSearchParams(savedFilters);
    }
    // Default filter if nothing is saved
    return new URLSearchParams({ status: 'Active' });
  });

  // State for API query variables (derived from filtersValue)
  const [apiVariables, setApiVariables] = useState(() =>
    buildApiVariables(filtersValue)
  );

  // Function to build variables from filters
  function buildApiVariables(params: URLSearchParams) {
    const status =
      params.getAll('status').length > 0
        ? (params.getAll('status') as Status[])
        : [Status.Active, Status.Inactive];
    // Extract type filter if present -
    // const type =
    //   params.getAll('type').length > 0
    //     ? (params.getAll('type') as ItemType[])
    //     : undefined; // Or null/empty array depending on API needs

    return { status /* , type */ }; // Include type in returned variables -
  }

  // Update variables when filters change
  useEffect(() => {
    setApiVariables(buildApiVariables(filtersValue));
  }, [filtersValue]);

  // Get fetching and error state from useQuery
  const [{ data, fetching, error }, reexecuteQuery] = useQuery({
    query: ItemsQueryDocument,
    variables: apiVariables,
  });

  const reload = () => reexecuteQuery({ requestPolicy: 'network-only' });
  const { results, search } = useSearch(data?.items, SEARCH_OPTIONS);
  const itemsToDisplay = useMemo(() => {
    // Return empty array if data is explicitly undefined/null before first fetch
    if (data?.items === undefined) return [];
    if (searchValue) return results ?? [];
    return data?.items ?? [];
  }, [data?.items, results, searchValue]);
  const { sorted } = useSort(itemsToDisplay, { default: 'name' });

  // Calculate activeFilters state
  const areFiltersActive = useMemo(() => {
    // Check if there are any filters other than the default 'status=Active'
    let count = 0;
    let hasNonDefaultStatus = false;
    for (const [key, value] of filtersValue.entries()) {
      if (key === 'status' && value !== 'Active') {
        hasNonDefaultStatus = true;
      }
      if (key !== 'status') {
        count++;
      }
    }
    // Consider filters active if there are non-status filters OR if status is not just 'Active'
    return (
      count > 0 ||
      filtersValue.getAll('status').length !== 1 ||
      hasNonDefaultStatus
    );
  }, [filtersValue]);

  // Determine DisplayState
  const displayState = useMemo((): DisplayState => {
    // Use useRef to track initial load? Not strictly needed if loading looks the same
    if (fetching && data?.items === undefined) return 'INITIAL_LOADING';
    if (fetching) return 'LOADING';
    if (error) return 'ERROR';
    if (!data?.items || itemsToDisplay.length === 0) return 'NO_RESULTS';
    return 'IDLE';
  }, [fetching, data?.items, error, itemsToDisplay.length]);

  // Handle filter/search changes from FilterGroupSearch
  const handleFilterChange = useCallback(
    ({
      searchValue: newSearchValue,
      filtersValue: newFiltersValue,
    }: {
      searchValue: string;
      filtersValue: URLSearchParams;
    }) => {
      try {
        // Save filters to local storage
        ls.set('items.filters', newFiltersValue.toString());
        // Update state
        setSearchValue(newSearchValue);
        setFiltersValue(newFiltersValue);
        // Trigger client-side search if needed
        search(newSearchValue);
      } catch (error) {
        console.error('Error processing item filters:', error);
      }
    },
    [ls, search] // Added ls and search dependencies
  );

  const getListItemProps = (data: Item) => {
    const item = getFragmentData(ItemData_ItemFragment, data);

    return {
      id: item.id,
      linkTo: `/items/${item.id}`,
      image: item.image
        ? item.image
        : item.skus.find((sku) => sku.image)?.image,
      titleText: item.name,
      secondaryText: t('itemType.' + item.type),
      tertiaryText: variantsListString(item),
      // hero: {
      //   number: item.activeSkus,
      //   directive: t('variant', { count: item.activeSkus }),
      // },
      status: item.status,
    };
  };

  console.log(data?.items);

  return (
    <AuthGate action='manage' subject='feat.items'>
      <NavContainer nav={<StockNav />}>
        <NavContainer.FilterRow ref={filterRowRef}>
          <ItemFilters
            value={{ searchValue, filtersValue }}
            onChange={handleFilterChange}
          />
        </NavContainer.FilterRow>
      </NavContainer>
      <ListDetailView
        nav={<StockNav />}
        actions={<CreateButton to='new'>{t('item')}</CreateButton>}
        count={data?.items?.length}
        dynamicMarginTop={dynamicMarginTop}
        main={
          <StatefulView
            displayState={displayState}
            activeFilters={areFiltersActive}
            isSearchResultEmpty={
              displayState === 'NO_RESULTS' &&
              !!searchValue &&
              !!data?.items &&
              data.items.length > 0
            }
          >
            {/* Content for IDLE state */}
            <MatchList
              items={sorted ? sorted.map(getListItemProps) : []}
              matchPath='/items/:id/*'
              className='pb-24 lg:pb-4'
            />
          </StatefulView>
        }
      >
        <Route path='*' element={<FloatingActionButton />} />
        <Route
          path='/new/*'
          element={<ItemFormModal action={() => {}} title={t('newItem')} />}
        />
        <Route
          path='/:id/edit'
          element={<ItemFormModal action={() => {}} title={t('editItem')} />}
        />
        <Route
          path='/:id/*'
          element={<ItemViewModal data={data} reload={reload} />}
        />
      </ListDetailView>
    </AuthGate>
  );
};

function variantsListString(item: Item) {
  const { skus } = getFragmentData(ItemData_ItemFragment, item);

  return skus.length === 1
    ? skus[0].code
    : skus
        .reduce(
          (arr: string[], sku) =>
            sku.status === 'Active' ? arr.concat(sku._name ?? '') : arr,
          []
        )
        .join(', ');
}
